import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto';

const COLORS = {
  primary: '#2A304B',
  secondary: '#65AAE8',
  accent: '#FD9843',
  success: '#75B798',
  error: '#AB296A'
}
export default class extends Controller {
  static targets = ["mapsCreatedChart", "activeVsDormantOverallChart",
                   "activeVsDormantCreatorChart", "activeVsDormantSupporterChart", "topCategoriesChart",
                   "assetsAddedChart", "assetsPerMapChart", "formSubmissionsChart",
                   "newUsersChart", "collaborationChart"]

  connect() {
    this.initializeCharts()
  }

  initializeCharts() {
    Chart.defaults.font.family = "'Outfit', sans-serif"

    this.initializeMapsCreatedChart();
    this.initializeActiveVsDormantCharts();
    this.initializeTopCategoriesChart();
    this.initializeAssetsAddedChart();
    this.initializeAssetsPerMapChart();
    this.initializeFormSubmissionsChart();
    this.initializeNewUsersChart();
    this.initializeCollaborationChart();
  }

  initializeMapsCreatedChart() {
    const mapsData = JSON.parse(this.mapsCreatedChartTarget.dataset.chartData)

    const labels = mapsData.labels

    const datasets = []

    const defaultDataset = { borderWidth: 2, fill: false, tension: 0.4 }

    datasets.push({
      label: 'Total Maps',
      data: mapsData.overall,
      borderColor: COLORS.secondary,
      backgroundColor: COLORS.secondary + '20',
      ...defaultDataset
    })

    datasets.push({
      label: 'Person-Centered Maps',
      data: mapsData.creator,
      borderColor: COLORS.primary,
      backgroundColor: COLORS.primary + '20',
      ...defaultDataset
    })

    datasets.push({
      label: 'Community Maps',
      data: mapsData.supporter,
      borderColor: COLORS.error,
      backgroundColor: COLORS.error + '20',
      ...defaultDataset
    })

    new Chart(this.mapsCreatedChartTarget, {
      type: 'line',
      data: { labels, datasets },
      options: {
        ...this.getChartOptions(),
        plugins: {
          ...this.getChartOptions().plugins,
          legend: {
            display: true,
            position: 'bottom'
          }
        }
      }
    })
  }

  initializeActiveVsDormantCharts() {
    this.makeDonutChart(this.activeVsDormantOverallChartTarget, COLORS.primary)
    this.makeDonutChart(this.activeVsDormantCreatorChartTarget, COLORS.accent)
    this.makeDonutChart(this.activeVsDormantSupporterChartTarget, COLORS.secondary)
  }

  initializeTopCategoriesChart() {
    this.makeBarChart(this.topCategoriesChartTarget, "Top Categories by Asset Count", [COLORS.primary, COLORS.secondary, COLORS.accent, COLORS.success, COLORS.error])
  }

  initializeAssetsAddedChart() {
    this.makeLineChart(this.assetsAddedChartTarget, "Assets Added", COLORS.primary)
  }

  initializeAssetsPerMapChart() {
    this.makeBarChart(this.assetsPerMapChartTarget, "Assets per Map Distribution", COLORS.secondary)
  }

  initializeFormSubmissionsChart() {
    this.makeLineChart(this.formSubmissionsChartTarget, "Form Submissions", COLORS.primary)
  }

  initializeNewUsersChart() {
    this.makeBarChart(this.newUsersChartTarget, "New Users", COLORS.secondary)
  }

  initializeCollaborationChart() {
    this.makeLineChart(this.collaborationChartTarget, "Collaboration Events", COLORS.secondary)
  }

  makeDonutChart(target, color) {
    let dataset = JSON.parse(target.dataset.chartData)
    new Chart(target, {
      type: 'doughnut',
      data: {
        labels: ['Active Maps', 'Dormant Maps'],
        datasets: [{
          data: [dataset.active, dataset.dormant],
          backgroundColor: [color, '#e0e0e0'],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' }
        }
      }
    })
  }

  makeBarChart(target, title, colors) {
    let dataset = JSON.parse(target.dataset.chartData)
    new Chart(target, {
      type: 'bar',
      data: {
        labels: Object.keys(dataset),
        datasets: [{
          label: title,
          data: Object.values(dataset),
          backgroundColor: colors
        }]
      },
      options: this.getChartOptions()
    })
  }

  makeLineChart(target, title, color) {
    let dataset = JSON.parse(target.dataset.chartData)
    new Chart(target, {
      type: 'line',
      data: {
        labels: Object.keys(dataset),
        datasets: [{
          label: title,
          data: Object.values(dataset),
          borderColor: color,
          backgroundColor: color + '20',
          fill: true,
          tension: 0.4
        }]
      },
      options: this.getChartOptions()
    })
  }

  getChartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            precision: 0,
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    }
  }
}